// Angular base libraries
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, of, throwError, forkJoin } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';

import {
    Address, CountryForBilling, StateAddress, CityAddress, DistrictAddress,
    AddressFormattedCheckout, CountryAddress, AddressSuggestedInfo,
} from './address.model';
import { AddressTransformer } from './address.transformer';
import { addDistrictToAddress } from './uses-cases/address-district.use-case';

import { CountryService } from '@api/country/country.service';
import { DroppointListsDetail } from '@api/store-locator/droppoint-lists/models/droppoint-lists.models';
import { BH, EG, JO, KOREA } from '@core/domain/constants/markets.const';
import { MarketConfigurable } from '@core/domain/impl/market-configurable.impl';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { StoreXconfigurationsObject } from '@core/domain/models/store.models';
import { RequestParams } from '@core/infrastructure/http-requests/http-requests.service';
import { LoadStoreList } from '@core/infrastructure/state/core-commands.actions';
import { getCountriesAndPhonePrefixes, getCountryCode, getCountryGroups, getXConfigs, getXConfigurationValue } from '@core/infrastructure/state/core.selectors';
import { ApiAddresses, ApiAddressService } from '@features/address';
import { User } from '@features/user';
import { CountryGroupItem, StoreDetail } from '@mdfrontw/core';
import { TaxCodeParticular } from '@shared/address/address-form/components/dropdown-tax-code/dropdown-tax-code.models';
import { ZIP_CODE_PATTERNS } from '@shared/address/address-form/components/input-zipcode/zip-code-patterns';
import { getAddressTaxCode, getShippingAddressesDelivery } from '@shared/address/store/address/address.selectors';
import { AddressConfigState } from '@shared/address/store/address/address.state';
import { DropdownOption } from '@shared/forms/models/input-models/input-dropdown.model';
import { getUserSelector } from '@shared/user/store';

const TK_DEFAULT_ZIPCODE = '34555';
const TK_DEFAULT_CHECK = /^null$|^undefined$|^-$|^$/;
const STATE_REGEX = /^\s+/;
const MULTIPLE_STATE_REGEX = /\s+$/;

// TODO ECOMDUTI-21426: [TechDebt] - A este servicio demasiada funcionalidad.
// - simplificar
// - Refactorizar para extraer funciones puras a casos de uso
// - Refactorizar para mover a su propio módulo feature
// - Eliminar como proveedor root

/**
 * Servicios general de Address
 */
@Injectable({
    providedIn: 'root',
})
export class AddressService extends MarketConfigurable<Record<string, (address: Address) => any>> {
    protected readonly config = {
        AE: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        BH: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        CO: {
            countryBasedBillingValidator: (address: Address) => {
                return !!(address.taxCodeCode && address.taxRegimeCode && address.documentTypeCode);
            },
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        DO: {
            countryBasedBillingValidator: (address: Address) => {
                return !!address.documentTypeCode;
            },
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        PE: {
            countryBasedBillingValidator: (address: Address) => {
                return !!address.documentTypeCode;
            },
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        EG: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        IL: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        JO: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        KW: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        LB: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        MA: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        MX: {
            countryBasedBillingValidator: (address: Address) => {
                return !!(address.taxCodeCode && address.taxRegimeCode);
            },
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        QA: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: (address: Address) => {
                const clonedAddress: Address = this.preFormatAddress(address);
                return this.getFormatAddressQatar(clonedAddress);
            },
            defaultZipCode: (value) =>{
                return value;
            },
        },
        RS: {
            countryBasedBillingValidator: (address: Address) => {
                return !!(address.vatin && address.documentTypeCode);
            },
            isBillingAddressComplete: (address: Address) => {
                return address?.addressType === 'SB';
            },
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        TR: {
            countryBasedBillingValidator: (address: Address) => {
                return address.zipCode && address.zipCode !== '-';
            },
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value: Address) =>{
                const returnValue = value;
                if (TK_DEFAULT_CHECK.test(value?.zipCode)) {
                    returnValue.zipCode = TK_DEFAULT_ZIPCODE;
                }
                return returnValue;
            },
        },
        VN: {
            countryBasedBillingValidator: () => true,
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
        default: {
            countryBasedBillingValidator: (address: Address) => {
                return address.zipCode && address.zipCode !== '-';
            },
            isBillingAddressComplete: this.isBillingAddressComplete.bind(this),
            addressFormatter: this.getFormatAddressDefault.bind(this),
            defaultZipCode: (value) =>{
                return value;
            },
        },
    };

    // TODO ECOMDUTI-20991: [TechDebt] - Almacenar datos en un servicio va en contra del patrón redux. Reemplazar funcionalidad.
    private readonly _addresses: Address[];

    public countriesForBilling: CountryForBilling[] = [];

    public stateAddresses: StateAddress[] = [];

    public cityAddresses: CityAddress[] = [];

    public districtAddress: DistrictAddress[] = [];

    public stateDropdownOptions: DropdownOption[] = [];

    public addressInvoiceData: any;

    public userState: User;

    public countryConfigCache: any;

    private requestParameters: RequestParams;

    private readonly countriesPrefixDropdownValues$: BehaviorSubject<DropdownOption[]> = new BehaviorSubject(undefined);

    constructor(
        private readonly store: Store,
        private readonly apiAddressService: ApiAddressService,
        private readonly countryService: CountryService,
        private readonly translate: TranslateService,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {
        super();

        // TODO ECOMDUTI-15592 Ver donde colocar este servicio y qué requiere de API
        this.store.dispatch(LoadStoreList({ force: false }));

        this.store.select(getUserSelector).pipe(filter(user => !!user))
            .subscribe((userState: User) => { this.userState = userState; });
    }

    // TODO ECOMDUTI-21426: [TechDebt] - extraer a caso de uso en alguna feature a determinar
    public static getFullName(firstName: string, middleName?: string, lastName?: string): string {
        return firstName + (middleName ? ` ${middleName}` : '') + (lastName ? ` ${lastName}` : '');
    }

    public getCountriesPrefixDropdownValues$(): Observable<DropdownOption[]> {
        // eslint-disable-next-line rxjs/no-subject-value
        if (!this.countriesPrefixDropdownValues$.value) {
            return this.getCountriesPrefix();
        }
        return this.countriesPrefixDropdownValues$.asObservable();
    }

    public setCountriesPrefixDropdownValues$(countriesDropdownValues: DropdownOption[]): void {
        this.countriesPrefixDropdownValues$.next(countriesDropdownValues);
    }

    public getAddresses(): Observable<Address[]> {
        this.requestParameters = [
            {
                key: 't',
                value: Date.now().toString(),
            },
        ];

        const sortByPreferredFn = (a: Address, b: Address): number => {
            if (a.preferred > b.preferred) {
                return -1;
            }
            if (a.preferred < b.preferred) {
                return 1;
            }
            return 0;
        };

        return this.apiAddressService.getAddresses(this.requestParameters).pipe(
            map((apiAddresses: ApiAddresses) => AddressTransformer.addressesFromApi(apiAddresses)),
            switchMap((addresses: Address[]) => {
                const formattedAddresses$ = addresses.map((address) =>
                    forkJoin({
                        address: of(address),
                        addressFormattedCheckout: this.getAddressFormatted(address),
                    }).pipe(map((result) => ({
                        ...address,
                        addressFormattedCheckout: result.addressFormattedCheckout,
                    }))));

                return forkJoin(formattedAddresses$).pipe(map((x) => x));
            }),
            map((addresses) => addresses.sort(sortByPreferredFn)),
        );

        // return this.apiAddressService.getAddresses(this.requestParameters).pipe(
        //     map((apiAddresses: ApiAddresses) => {
        //         return AddressTransformer.addressesFromApi(apiAddresses).map((item: Address) =>
        //             ({ ...item, addressFormattedCheckout: this.getAddressFormatted(item) }))
        //             .sort(sortByPreferredFn);
        //     })
        // );
    }

    public getAddress(addressId: string, isGuest = false): Observable<Address> {
        if (isGuest) {
            return this.apiAddressService.getAddressGuest(addressId).pipe(map(apiAddress => {
                return AddressTransformer.addressFromApi(apiAddress);
            }));
        }
        return this.apiAddressService.getAddress(addressId).pipe(map(apiAddress => {
            return AddressTransformer.addressFromApi(apiAddress);
        }));
    }

    public getAddressesDelivery(): Observable<Address[]> {
        return combineLatest([
            this.store.select(getCountryCode),
            this.countryService.isWWStore$,
        ]).pipe(switchMap(([countryCode, isWWStore]) => {
            return this.store.select(getShippingAddressesDelivery(countryCode, isWWStore));
        }));
    }

    public getAddressFormatted(address: Address): Observable<AddressFormattedCheckout> {
        const config = this.getConfigByMarketCode(address.countryCode);

        return config.addressFormatter(address);
    }

    public removeInitialId(value: string): string {
        return value.replace(/\d/g, '').replace(' ', '').replace(/#/g, '');
    }

    private getFormatAddressQatar(address: Address, hideAdditionlaData = false): Observable<AddressFormattedCheckout> {
        const addressFormatted: AddressFormattedCheckout = {
            addressNumberLine: '',
            zipCodeStateLine: '',
        };

        const addressAditional = address.addressLines.length >= 2 ? address.addressLines[1] : '';

        // se eliminan carácteres no necesarios que llegan de back-end
        const city = this.removeInitialId(address.city);
        const colony = this.removeInitialId(address.colony);

        // TODO ECOMDUTI-20991 estos campos de Qatar son necesarios, por tanto ninguno de ellos podrá ser undefined
        if (address.unitNumber !== undefined) {
            return this.translate.get([
                'dev.forms.unitNumber',
                'dev.forms.zoneNumber',
                'dev.forms.streetNumber',
                'dev.forms.buildingNumber',
            ]).pipe(
                first(),
                map((translations) => {
                    const unitNumberString = `${translations['dev.forms.unitNumber']} ${address.unitNumber}`;
                    const zoneNumberString = `${translations['dev.forms.zoneNumber']} ${address.zoneNumber}`;
                    const streetNumberString = `${translations['dev.forms.streetNumber']} ${address.addressLines[0]}`;
                    const buildingNumberString = `${translations['dev.forms.buildingNumber']} ${address.buildingNumber}`;

                    return {
                        haveAddressLines: true,
                        addressNumberLine: `${unitNumberString}, ${zoneNumberString}, ${streetNumberString}, ${buildingNumberString}`,
                        zipCodeStateLine: `${addressAditional}${addressAditional ? ', ' : ''}${hideAdditionlaData ? '' : `${city} - ${colony}`}`,
                    };
                }),
            );
        }

        return of(addressFormatted);
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    private getFormatAddressDefault(address: Address): Observable<AddressFormattedCheckout> {
        const countryCode = address.countryCode;

        const clonedAddress: Address = this.preFormatAddress(address);
        // TODO ECOMDUTI-20991 Eliminar referencias a paises en código
        if (countryCode === 'AE' && clonedAddress.zipCode) {
            clonedAddress.zipCode = '';
        }

        const addressFormatted: AddressFormattedCheckout = {
            addressNumberLine: clonedAddress.addressLines[0],
            zipCodeStateLine: '',
            haveAddressLines: true,
            phoneNumber: '',
        };

        if (
            countryCode === BH
            || countryCode === JO
            || countryCode === EG
        ) {
            // se eliminan carácteres no necesarios que llegan de back-end
            if (!clonedAddress.city) {
                clonedAddress.city = '';
            }
            const city = this.removeInitialId(clonedAddress.city);

            if (clonedAddress.stateName && city) {
                addressFormatted.zipCodeStateLine = `${clonedAddress.stateName}, ${city}`;
            } else {
                addressFormatted.zipCodeStateLine = clonedAddress.stateName || city;
            }
            return of(addressFormatted);
        }

        const secondLine = [
            clonedAddress?.zipCode !== '-' ? clonedAddress?.zipCode : null,
            countryCode === KOREA ? null : clonedAddress?.city,
            clonedAddress?.stateName,
        ];

        addressFormatted.zipCodeStateLine = secondLine.filter(value => !!value).join(' ');

        if (addressFormatted?.addressNumberLine === '-' || addressFormatted?.zipCodeStateLine === '-') {
            addressFormatted.haveAddressLines = false;
            if (clonedAddress.phones?.[0]?.subscriberNumber) {
                addressFormatted.phoneNumber = `${clonedAddress.phones[0].prefixCode} ${clonedAddress.phones[0].subscriberNumber}`;
            }
        }

        return of(addressFormatted);
    }

    /**
     * Preprara la lista direcciónes a almacenar siguiendo las siguientes reglas de negocio:
     * - Si el usuario no tiene dirección de billing completa se crea un clon de la dirección de shipping y devuelve la
     *   dirección de shipping y la clonada de billing.
     * - Si el usuario ya cuenta con una dirección de billing clonada o una direccion de billing de pais distinto, devuelve
     *   la dirección de shipping.
     * - Si la dirección y la de billing pertenecen al mismo pais y no cumple con ninguna de las anteriores se crea una copia
     * - Por defecto no devolverá nada
     *
     * @param shippingAddress nueva dirección de shipping
     * @param addresses listado de direcciones del usuario
     * @param countryCode mercado actual
     * @returns listado de direcciones a crear
     */

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    public getShippingAddressToSave(
        shippingAddress: Address,
        userAddresses: Address[],
        countryCode: string,
    ): Address[] {
        const countryBasedConfig = this.getConfigByMarketCode(countryCode);

        const newAddress = {
            ...shippingAddress,
            addressName: 'billingAddress#',
        };
        const _billingAddress = userAddresses.find((address: Address) => address.addressType === 'SB');

        const billingAddress = { ..._billingAddress };

        if (!newAddress?.addressType || newAddress?.addressType === 'SB') {
            newAddress.addressType = 'S';
            newAddress.isShippingAddress = true;
        }

        const addressCloned = this.billingAddressHasBeenCloned(userAddresses);

        const billingSameCountry = billingAddress && billingAddress.countryCode === countryCode;

        const billingIsComplete = countryBasedConfig.isBillingAddressComplete(billingAddress);

        if (countryCode === 'CN' && newAddress.stateName) {
            billingAddress.municipality = newAddress.stateName;
            newAddress.municipality = newAddress.stateName;
        }

        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        if (!billingAddress || !billingIsComplete) {
            const newBillingAddress: Address = JSON.parse(JSON.stringify(newAddress));
            // biome-ignore lint/performance/noDelete: <explanation>
            delete newBillingAddress.id;
            newBillingAddress.addressType = 'SB';
            newBillingAddress.isShippingAddress = null;
            newBillingAddress.addressName = `${newBillingAddress.email || newBillingAddress.addressLines[0]}|Dutti${countryCode}`;

            return [newBillingAddress, newAddress];
        }

        if (addressCloned || !billingSameCountry) {
            return [newAddress];
        }

        if (billingSameCountry /* || CountryService.isWWCountry() */) { // TODO ECOMDUTI-20991 implement isWWCountry
            // VERSIÓN MINIFICADA
            if (this.addressAbleToClone(billingAddress)) {
                const newShippingAddressFromBilling = JSON.parse(JSON.stringify(billingAddress));
                newShippingAddressFromBilling.addressName = 'billingAddress#';

                return [newShippingAddressFromBilling, newAddress];
            }

            const billingAddresToSave: Address = { ...billingAddress, ...newAddress };
            // biome-ignore lint/performance/noDelete: <explanation>
            delete billingAddresToSave.id;
            billingAddresToSave.addressName = `${billingAddresToSave.email || billingAddresToSave.addressLines[0]}|Dutti${
                countryCode
            }`;
            billingAddresToSave.addressType = 'SB';
            billingAddresToSave.isShippingAddress = null;

            newAddress.email = billingAddress.email;
            /* newAddress = _.merge({},billingAddress,newAddress);
            delete newAddress.id;
            delete newAddress.invoice;
            delete newAddress.phones[0].prefixCode;
            */
            newAddress.municipality = newAddress.municipality === '-' ? '' : newAddress.municipality;
            newAddress.colony = newAddress.colony === '-' ? '' : newAddress.colony;

            return [billingAddresToSave, newAddress];
        }

        return [];
    }

    public saveAddresses(addresses: Address[]): Observable<Address> {
        const config = this.getConfigByMarketCode(addresses[0].countryCode);
        let shippingAddress = addresses.length === 1
            ? config.defaultZipCode(addresses[0])
            : config.defaultZipCode(addresses[1]);

        // [ECOMDUTI-14933]: Se parchea la dirección para cambiar el valor de campos inconsistentes
        if (shippingAddress.dropType) {
            shippingAddress = this.fixZipCodeAndCity(shippingAddress);
        }
        shippingAddress = config.defaultZipCode(shippingAddress);
        if (addresses.length === 1) {
            return this.apiAddressService.saveAddress(shippingAddress).pipe(
                map((response: any) => {
                    // TODO ECOMDUTI-20991 [TechDebt] - Se debería hacer mediante destructuring, pero al usar la referencia shippingAddress = addresses[0] no funciona correctamente en el efecto SaveShippingAddress
                    shippingAddress.id = response.id;
                    return shippingAddress;
                }),
            );
        }
        if (addresses.length > 1) {
            const billingAddress = config.defaultZipCode(addresses[0]);
            // TODO ECOMDUTI-20991 Las llamadas se pueden hacer en paralelo
            return this.apiAddressService.saveAddress(billingAddress).pipe( // No se actualiza el ID del billing, se arregla porque después de hace un LoadAddresses
                switchMap(() => this.apiAddressService.saveAddress(shippingAddress)),
                map((response: any) => {
                    // TODO ECOMDUTI-20991 [TechDebt] - Se debería hacer mediante destructuring, pero al usar la referencia shippingAddress = addresses[0] no funciona correctamente en el efecto SaveShippingAddress
                    shippingAddress.id = response.id;
                    return shippingAddress;
                }),
            );
        }
        return throwError(() => ({}));
    }

    public getBillingAddressToSave(
        _newAddress: Address,
        addresses: Address[],
        _billingAddress: Address,
        countryCode: string,
    ): Address[] {
        const actions: Address[] = [];
        const newAddress = { ..._newAddress };

        const addressCloned = this.billingAddressHasBeenCloned(addresses);
        newAddress.isShippingAddress = null;
        // TODO ECOMDUTI-20991 [TechDebt] - Reemplazar método obsoleto
        // eslint-disable-next-line deprecation/deprecation
        newAddress.addressName = `${newAddress.email || newAddress.addressLines[0]}|${this.window.inditex?.iStoreIdentifier}`;

        // TODO ECOMDUTI-20991 [TechDebt] - Reemplazar método obsoleto
        // eslint-disable-next-line deprecation/deprecation
        if (this.window.inditex?.iCountryCode === 'CN' && newAddress.stateName) {
            newAddress.municipality = newAddress.stateName;
        }

        if (newAddress.countryCode === countryCode) {
            actions.push(newAddress);
            if (!addressCloned && this.addressAbleToClone(newAddress)) {
                const newShippingAddressFromBilling = this.cloneAddress(newAddress);
                actions.push(newShippingAddressFromBilling);
            }
        } else {
            actions.push(newAddress);
        }

        return actions;
    }

    public getDroppointAddressToSave(_newAddress: Address, _billingAddress: Address): Address[] {
        const newAddress = { ..._newAddress };
        let billingAddress = { ..._billingAddress };
        newAddress.addressName = `pickup#${newAddress.email}`;

        if (this.isBillingAddressCompleteForDroppoint(billingAddress)) {
            newAddress.addressType = 'S';
            newAddress.isShippingAddress = true;
            return [newAddress];
        }

        if (billingAddress) {
            billingAddress.addressType = 'SB';
            billingAddress.firstName = newAddress.firstName;
            billingAddress.lastName = newAddress.lastName;
            billingAddress.phones = JSON.parse(JSON.stringify(newAddress.phones));
        } else {
            billingAddress = JSON.parse(JSON.stringify(newAddress));
            billingAddress.addressType = 'SB';
            billingAddress.zipCode = '';
            billingAddress.addressLines[0] = '-';

            if (billingAddress.addressLines[1]) {
                billingAddress.addressLines[1] = '-';
            }

            billingAddress.city = '';
            billingAddress.stateCode = '-';

            // Delete droppoint related information.
            // biome-ignore lint/performance/noDelete: <explanation>
            delete billingAddress.isShippingAddress;
            // biome-ignore lint/performance/noDelete: <explanation>
            delete billingAddress.dropPointName;
            // biome-ignore lint/performance/noDelete: <explanation>
            delete billingAddress.idDropPoint;
            // biome-ignore lint/performance/noDelete: <explanation>
            delete billingAddress.idDropPointInterno;
            // biome-ignore lint/performance/noDelete: <explanation>
            delete billingAddress.isShippingAddress;
            // biome-ignore lint/performance/noDelete: <explanation>
            delete billingAddress.dropType;
        }

        return [billingAddress, newAddress];
    }

    public getPickUpAddressToSave(_newAddress: Address/* , context: EcommerceContext */): Address[] {
        const newAddress = { ..._newAddress };
        newAddress.addressName = `pickup#${newAddress.email}`;

        newAddress.addressType = 'SB';

        newAddress.isFullAddress = true;

        // const address: Address = this.getBillingFormData(formData, 'store');
        return [newAddress];
    }

    public isBillingAddressCompleteForDroppoint(_clonedBillingAddress: Address): boolean {
        if (!_clonedBillingAddress) {
            return false;
        }
        const clonedBillingAddress = { ..._clonedBillingAddress };
        if (
            clonedBillingAddress.email !== ''
            && clonedBillingAddress.firstName !== ''
            && clonedBillingAddress.lastName !== ''
            && clonedBillingAddress.phones[0].subscriberNumber
            && clonedBillingAddress.phones[0].subscriberNumber.length > 1
            && clonedBillingAddress.phones[0].prefixCode
            && clonedBillingAddress.phones[0].prefixCode.length > 1
        ) {
            return true;
        }
        return false;
    }

    public fillHiddenFields(addressData: Address, isWW: boolean): Address {
        this.addressInvoiceData = addressData;

        const address = { ...addressData };

        if (address.state && isWW) {
            address.stateCode = address.state;
            address.stateName = address.state;
            address.state = undefined;
        } else if (!address.stateCode || address?.stateCode === `${address.countryCode}NONE` || address?.stateCode === '-') {
            address.stateCode = `${address.countryCode}NONE`;
            address.stateName = `${address.countryCode}NONE`;
        }

        return address;
    }

    public getCountriesForDropdown(): Observable<DropdownOption[]> {
        return this.store.select(getCountriesAndPhonePrefixes).pipe(
            first((countries) => !!countries),
            map((countryAddresses: CountryAddress[]) => {
                return AddressTransformer.countryToDropdownValues(countryAddresses);
            }),
        );
    }

    public getCountriesForBilling(): Observable<CountryForBilling[]> {
        return this.store.select(getCountryGroups).pipe(
            first((countries) => !!countries),
            map((data: any) => {
                this.countriesForBilling = AddressTransformer.countriesForBillingFromApi(data[0]?.countries);
                return this.countriesForBilling;
            }),
        );
    }

    public getCountriesPrefix(): Observable<DropdownOption[]> {
        return this.store.select(getCountriesAndPhonePrefixes).pipe(
            first((countries) => !!countries),
            map((countryPrefix: CountryAddress[]) => AddressTransformer.countryPrefixToDropdownValues(countryPrefix)),
            tap((dropdownOptions: DropdownOption[]) => { this.setCountriesPrefixDropdownValues$(dropdownOptions); }),
        );
    }

    public getStates(storeId?: number | string): Observable<StateAddress[]> {
        const currentStoreId: string = storeId ? storeId.toString() : undefined;
        return this.apiAddressService.getStateAddresses(currentStoreId).pipe(
            first(),
            map((data: any) => {
                this.stateAddresses = AddressTransformer.stateAddressesFromApi(data);
                return this.stateAddresses;
            }),
        );
    }

    public getStateDropdownValues(storeId?: number | string): Observable<DropdownOption[]> {
        return this.getStates(storeId).pipe(
            first(),
            map((stateAddress: StateAddress[]) => AddressTransformer.stateAddressesToDropdownValues(stateAddress)),
            tap((dropdownOptions: DropdownOption[]) => {
                this.stateDropdownOptions = dropdownOptions;
            }),
        );
    }

    public getStateDropdownOptions(): DropdownOption[] {
        return this.stateDropdownOptions;
    }

    public getCities(stateCode: string): Observable<CityAddress[]> {
        return this.apiAddressService.getCityAddresses(stateCode).pipe(
            first(),
            map((data: any) => {
                this.cityAddresses = AddressTransformer.cityAddressesFromApi(data);

                return this.cityAddresses;
            }),
        );
    }

    public getCityDropdownValues(stateCode: string): Observable<DropdownOption[]> {
        return this.store.select(getXConfigurationValue('enableCities')).pipe(
            filter((enableCities) => enableCities === 'true'),
            switchMap(() =>
                this.getCities(stateCode).pipe(
                    first(),

                    map((cityAddresses: CityAddress[]) => AddressTransformer.cityAddressesToDropdownValues(cityAddresses)),
                )),
        );
    }

    public getDistricts(cityId: string): Observable<DistrictAddress[]> {
        return this.apiAddressService.getDistrictAddresses(cityId).pipe(
            first(),
            map((data: any) => {
                this.districtAddress = AddressTransformer.districtAddressesFromApi(data);

                return this.districtAddress;
            }),
        );
    }

    public getDistrictDropdownValues(cityId: string): Observable<DropdownOption[]> {
        return this.getDistricts(cityId).pipe(
            first(),
            map((districtAddresses: DistrictAddress[]) => {
                return AddressTransformer.districtAddressesToDropdownValues(districtAddresses);
            }),
        );
    }

    public getTaxCodeDropdownValues(taxRegimeTaxCodes): Observable<TaxCodeParticular[]> {
        return this.store.select(getAddressTaxCode).pipe(
            first(),
            map((taxCodes) => taxCodes.particular.filter(ptaxCode => taxRegimeTaxCodes.includes(ptaxCode.id))),
        );
    }

    public getCountryAddressByCountryCode$(countryCode: string): Observable<CountryAddress> {
        return this.store.select(getCountriesAndPhonePrefixes).pipe(
            first((countries) => !!countries),
            map((countries) => countries.find(
                (countryAddress: CountryAddress) => countryAddress.code === countryCode,
            )),
        );
    }

    public getCountryAddressByPhonePrefix(phonePrefix: string): Observable<CountryAddress> {
        return this.store.select(getCountriesAndPhonePrefixes).pipe(
            first((countries) => !!countries),
            map((countriesAndPrefixes) => {
                const countryAddressFiltered = countriesAndPrefixes.filter(
                    (countryAddress: CountryAddress) => countryAddress.phonePrefix === phonePrefix,
                );

                const matchCountry = countryAddressFiltered[0];

                return matchCountry;
            }),
        );
    }

    public getStateAddress(stateCode: string): StateAddress {
        return this.stateAddresses.find((stateAddress: StateAddress) => stateAddress.code === stateCode);
    }

    public deleteAddress(address: Address) {
        return this.apiAddressService.deleteAddress(address);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public checkWalletAddress(): any {}

    private cloneAddress(address): Address {
        // var billingAddress = _.clone(inputAddress);
        const shippingAddress = JSON.parse(JSON.stringify(address));
        // billingAddress.addressType ='SB';
        // billingAddress.isShippingAddress = null;
        // delete billingAddress.id;
        // delete billingAddress.droppointName;
        // delete billingAddress.idDropPoint;
        // billingAddress.addressName = billingAddress.email || billingAddress.addressLines[0];
        shippingAddress.addressType = 'S';
        shippingAddress.isShippingAddress = true;
        shippingAddress.addressName = 'billingAddress#';
        // biome-ignore lint/performance/noDelete: <explanation>
        delete shippingAddress.id;
        // biome-ignore lint/performance/noDelete: <explanation>
        delete shippingAddress.idDropPoint;

        return shippingAddress;
    }

    public billingAddressHasBeenCloned(addresses: Address[]): Address {
        return addresses.find((address) => {
            return address.addressType === 'S' && (address.addressName.includes('pickup#') || address.addressName.includes('billingAddress#'));
        });
    }

    public isBillingAddressComplete(address: Address): boolean {
        if (!address) {
            return false;
        }

        const config = this.getConfigByMarketCode(address.countryCode);

        if (
            address.firstName
            && address.lastName
            && address.countryCode
            && address.phones?.length > 0
            && address.phones[0].subscriberNumber
            && address.phones[0].subscriberNumber.length > 1
            && address.phones[0].prefixCode
            && address.phones[0].prefixCode.length > 1
            && address.addressLines?.length > 0
            && address.addressLines[0] !== '-'
            && address.city
            && address.city !== '-'
            && address.stateCode
            && address.stateCode !== '-'
            && config.countryBasedBillingValidator(address)
        ) {
            return true;
        }

        return false;
    }

    private addressAbleToClone(address): boolean {
        return (
            address.addressLines
            && address.addressLines[0] !== '-'
            && address.zipCode !== '-'
            && address.zipCode !== ''
            && address.stateCode !== '-'
            && address.stateCode !== ''
        );
    }

    public getCountryInfo(): any {
        return this.countryConfigCache;
    }

    // Si el código postal devuelto por google no coincide con la expresion regular
    // que el código postal tiene en ese país, no mostraremos la sugerencia para evitar
    // que se produzca error si continuamos en el tunel
    public incompleteSuggestedAddress(address: Record<string, string>, countryCode: string): boolean {
        const zipCodeKO = address.zipCode && !ZIP_CODE_PATTERNS[countryCode].test(address.zipCode);
        const addressKO = address.address && typeof address.address === 'undefined';

        return zipCodeKO
            || addressKO
            || typeof address.zipCode === 'undefined'
            || typeof address.city === 'undefined'
            || typeof address.province === 'undefined';
    }

    public cleanProvinceName(stateName: string): string {
        return stateName.replace('Girona', 'GERONA').replace('Ourense', 'ORENSE').replace('Lleida', 'LERIDA').toUpperCase();
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    public parseGeocode(geocode: google.maps.GeocoderResult, countryCode: string): Record<string, string> {
        const addressArray: Record<string, any> = {};

        for (const component of geocode.address_components) {
            if (this.componentIs(component, 'route')) {
                addressArray.address = component.long_name;
            }
            if (this.componentIs(component, 'street_number')) {
                addressArray.number = component.long_name;
            }
            if (this.componentIs(component, 'postal_code')) {
                addressArray.zipCode = component.long_name;
            }
            if (this.componentIs(component, 'country')) {
                addressArray.country = component.long_name;
            }
            if (this.componentIs(component, 'locality')) {
                addressArray.city = component.long_name;
            }
            if (!addressArray.city && this.componentIs(component, 'sublocality')) {
                addressArray.city = component.long_name;
            }
            if (
                !addressArray.province
                && (this.componentIs(component, 'administrative_area_level_2') || this.componentIs(component, 'administrative_area_level_1'))
            ) {
                addressArray.province = component.long_name;
            }
            if (this.componentIs(component, 'sublocality_level_1')) {
                addressArray.municipality = component.long_name;
            }
            if (this.componentIs(component, 'administrative_area_level_3')) {
                addressArray.colony = component.long_name;
            }

            // Special cases
            if ((countryCode === 'GB' || countryCode === 'SI') && this.componentIs(component, 'postal_town')) {
                addressArray.city = component.long_name;
            }
            if (
                (countryCode === 'IE'
                    || countryCode === 'CY'
                    || countryCode === 'DE'
                    || countryCode === 'EE'
                    || countryCode === 'HR'
                    || countryCode === 'MX'
                    || countryCode === 'NL'
                    || countryCode === 'PL'
                    || countryCode === 'SE'
                    || countryCode === 'US'
                    || countryCode === 'NO'
                    || countryCode === 'CA')
                && this.componentIs(component, 'administrative_area_level_1')
            ) {
                addressArray.province = component.long_name;
            }
            if (countryCode === 'CN' && this.componentIs(component, 'sublocality_level_1')) {
                addressArray.colony = component.long_name;
            }
            if (countryCode === 'MC' && this.componentIs(component, 'locality')) {
                addressArray.province = component.long_name;
            }
            if (countryCode === 'SK' && this.componentIs(component, 'sublocality_level_1')) {
                addressArray.province = component.long_name;
            }
        }

        return addressArray;
    }

    public fillAddressInfo(_suggestedAddress: Record<string, string>, address: Address): AddressSuggestedInfo {
        let addressAndNumber: string;
        let suggestedAddress = { ..._suggestedAddress };

        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        if (!suggestedAddress.province || !suggestedAddress.zipCode) {
            suggestedAddress = null;
        } else {
            // En caso contrario, obtenemos la provincia desde el servicio y establecemos la dirección junto al número
            suggestedAddress.province = this.cleanProvinceName(suggestedAddress.province);

            // Si la dirección de Google contiene número
            if (suggestedAddress.number) {
                // Añade información extra a la dirección en caso de contenerla
                if (address.addressLines.join(' ').split(suggestedAddress.number)[1]) {
                    addressAndNumber = `${suggestedAddress.address} ${suggestedAddress.number}${address.addressLines.join(' ').split(suggestedAddress.number)[1]}`;
                } else {
                    addressAndNumber = `${suggestedAddress.address} ${suggestedAddress.number}`;
                }

                // De lo contrario, la dirección devuelta será la de Google a secas
            } else {
                addressAndNumber = suggestedAddress.address;
            }
        }
        return { suggestedAddress, addressAndNumber, originalAddress: address };
    }

    public shouldShowSuggestion(
        suggestedAddress: Record<string, string>,
        originalAddress: Address,
        addressAndNumber: string,
    ): boolean {
        return (
            !suggestedAddress
            || originalAddress.zipCode !== suggestedAddress.zipCode
            || originalAddress.city?.toLowerCase() !== suggestedAddress.city?.toLowerCase()
            || originalAddress.stateName?.toLowerCase() !== suggestedAddress.province?.toLowerCase()
            || originalAddress.addressLines[0] !== addressAndNumber
        );
    }

    public addressToString(address: Address): string {
        return `${address.addressLines[0]}, ${address.zipCode} ${address.city} ${address.stateName}`;
    }

    private componentIs(component: any, type: any): boolean {
        for (const t of component.types) {
            if (t === type) return true;
        }
        return false;
    }

    public getStateByName(stateName: string, states: DropdownOption[]): DropdownOption {
        for (const state of states) {
            const names = state.value.split('/');
            for (let name of names) {
                name = name.replace(STATE_REGEX, '').replace(MULTIPLE_STATE_REGEX, '');
                if (name && stateName && this.compare(name, stateName)) {
                    return state;
                }
            }
        }
        return null;
    }

    // Devuelve el mismo string sin tildes
    private cleanText(_text: string): string {
        let text = _text;
        const acentos = 'ÃÀÁÄÂÈÉËÊÌÍÏÎÒÓÖÔÙÚÜÛãàáäâèéëêìíïîòóöôùúüûÑñÇçŠšŸÿýŽž';
        const original = 'AAAAAEEEEIIIIOOOOUUUUaaaaaeeeeiiiioooouuuunnccSsYyyZz';
        for (let i = 0; i < acentos.length; i++) {
            text = text.replace(acentos.charAt(i), original.charAt(i));
        }

        return text;
    }

    // Compara dos strings quitando acentos y obviando mayúsculas
    private compare(name1: string, name2: string): boolean {
        return this.cleanText(name1.toUpperCase()) === this.cleanText(name2.toUpperCase());
    }

    // TODO ECOMDUTI-20991 implementar el postnummer que sirve para alemania
    public fillDroppointAddress(
        _addressModel: Address,
        droppointInfo: DroppointListsDetail,
        addressConfig: AddressConfigState,
        storeDetail: StoreDetail,
        droppointType = 'DP',
    ): Address {
        let addressModel = { ..._addressModel };

        addressModel.droppoint = droppointInfo;
        addressModel.idDropPointInterno = droppointInfo.internalId;

        // if (iAmGuest) {
        //     addressModel.addressName = '';
        // } else {
        //     addressModel.addressName = addressModel.email ?
        //         addressModel.email + '|' + storeIdentifier : storeIdentifier;
        // }
        addressModel.dropPointName = droppointInfo.name;

        if (addressConfig.droppointEnabled) {
            addressModel.addressLines = [droppointInfo.street];
        } else if (storeDetail.countryCode === 'DE') {
            // ECOMDUTI-6319 - Línea original. Si es necesario, comentar el if y dejar sólo esta parte.
            addressModel.addressLines = [droppointInfo.id, ''];
        } else {
            addressModel.addressLines = [droppointInfo.street, droppointInfo.id];
        }

        addressModel.city = droppointInfo.city;
        addressModel.zipCode = droppointInfo.zipCode;
        addressModel.isShippingAddress = true;

        // avoid modifying billing addresses with droppoint addresses
        if (addressModel.addressType === 'SB') {
            addressModel.addressType = 'S';
            // biome-ignore lint/performance/noDelete: <explanation>
            delete addressModel.id;
        }

        addressModel.dropType = droppointType;
        // new values...
        if (addressConfig.droppointEnabled) {
            addressModel.idDropPoint = droppointInfo.id;
            if (addressModel.postnumber) {
                addressModel.idDropPointUser = addressModel.postnumber;
            }
        }
        // if the droppoint has stateCode, use item
        if (droppointInfo.stateCode && droppointInfo.stateCode !== '') {
            addressModel.stateCode = droppointInfo.stateCode;
            // biome-ignore lint/performance/noDelete: <explanation>
            delete addressModel.stateName;
        }

        addressModel = addDistrictToAddress(addressModel, droppointInfo?.district, storeDetail.countryCode);

        return addressModel;
    }

    public getConfigAddress(): Observable<AddressConfigState> {
        return combineLatest([
            this.store.select(getCountryGroups),
            this.countryService.isWWStore$,
            this.store.select(getUserSelector).pipe(filter(user => !!user)),
            this.apiAddressService.getConfigAddress(),
            this.store.select(getXConfigs),
        ]).pipe(
            first(),
            map(([countryGroups, isWWStore, user, config, xConfs]) => {
                const groups = countryGroups;
                const xconf: StoreXconfigurationsObject = xConfs;
                const guestUser: boolean = user.userType === 'G';
                return this.getConfigurationState(isWWStore, xconf, guestUser, config, groups);
            }),
        );
    }

    private getConfigurationState(
        isWw: boolean,
        xconf: StoreXconfigurationsObject,
        guestUser: boolean,
        addressConfig: any,
        _groups: CountryGroupItem[],
    ): AddressConfigState {
        const configurationState: AddressConfigState = {};

        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        configurationState.droppointEnabled = xconf.DROPPOINT_ENABLED_FRONT || false;
        // TODO ECOMDUTI-20991 [TechDebt] - geoBlockingEnabled no está siendo utilizada. Se ha migrado el valor desde legacy pero no la funcionalidad.
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        configurationState.geoBlockingEnabled = xconf.ENABLE_GEOBLOQUEO || false;
        // TODO ECOMDUTI-20991 [TechDebt] - showAutocomplete no está siendo utilizada. Se ha migrado el valor desde legacy pero no la funcionalidad.
        configurationState.showAutocomplete = false;
        // configurationState.phoneVerificationEnabled = guestUser && xconf.ENABLE_PHONE_VALIDATION || false;
        configurationState.replaceBilling = isWw && guestUser;
        // eslint-disable-next-line no-secrets/no-secrets
        // configurationState.isCountryEditable = xconf.ENABLE_GEOBLOQUEO && !!groups.filter((group) => group.type === countryGroupTypes.ZONAGEOGRAFICA);
        // configurationState.isBirthDateRequired = xconf.HIDE_YEAR_AND_GENDER_ENABLED || false;
        configurationState.isDocumentMandatory = addressConfig.itxStoreConfigAddressTO?.isDocumentMandatory;
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        configurationState.isBillingAddressMandatory = addressConfig.itxStoreConfigAddressTO?.isBillingAddressMandatory
         || false;
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        configurationState.taxCode = addressConfig.itxStoreConfigAddressTO?.taxCode || false;
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        configurationState.taxRegimes = addressConfig.itxStoreConfigAddressTO?.taxRegimes || false;
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        configurationState.taxRegimesType = addressConfig.itxStoreConfigAddressTO?.taxRegimesType || false;
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        configurationState.documentTypes = addressConfig.itxStoreConfigAddressTO?.documentTypes || false;
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        configurationState.documentTypeByType = addressConfig.itxStoreConfigAddressTO?.documentTypeByType || false;
        return configurationState;
    }

    private preFormatAddress(address: Address): Address {
        const clonedAddress: Address = JSON.parse(JSON.stringify(address));

        // se eliminan carácteres no necesarios que llegan de back-end
        if (clonedAddress.city) {
            clonedAddress.city = clonedAddress.city.substring(clonedAddress.city.indexOf('#') + 1);
        }

        if (clonedAddress.colony) {
            clonedAddress.colony = clonedAddress.colony.substring(clonedAddress.colony.indexOf('#') + 1);
        }

        return clonedAddress;
    }

    private fixZipCodeAndCity(address: Address): Address {
        return address?.dropType != null
            ? {
                ...address,
                zipCode: address.droppoint?.zipCode,
                city: address.droppoint?.city,
            }
            : { ...address };
    }
}
